import moment from "moment";
import React, { useState, useMemo } from "react";
import { AiFillDelete } from "react-icons/ai";

const searchContainerStyles = {
  position: "relative",
  width: "100%",
  maxWidth: "420px",
};

const searchInputStyles = {
  width: "100%",
  padding: "12px 44px 12px 44px",
  fontSize: "14px",
  fontWeight: 400,
  color: "#1a1a2e",
  backgroundColor: "#f8f9fc",
  border: "1.5px solid #e0e3eb",
  borderRadius: "12px",
  outline: "none",
  transition: "all 0.25s ease",
  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
};

const searchInputFocusStyles = {
  ...searchInputStyles,
  borderColor: "#6366f1",
  backgroundColor: "#fff",
  boxShadow: "0 0 0 3px rgba(99,102,241,0.12), 0 2px 8px rgba(99,102,241,0.08)",
};

const searchIconStyles = {
  position: "absolute",
  left: "14px",
  top: "50%",
  transform: "translateY(-50%)",
  color: "#9ca3af",
  pointerEvents: "none",
  transition: "color 0.25s ease",
};

const clearBtnStyles = {
  position: "absolute",
  right: "10px",
  top: "50%",
  transform: "translateY(-50%)",
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "4px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#9ca3af",
  transition: "all 0.2s ease",
};

const SearchIcon = ({ color = "#9ca3af" }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const ClearIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const HighlightText = ({ text = "", highlight = "" }) => {
  if (!highlight.trim()) return <>{text}</>;

  const regex = new RegExp(
    `(${highlight.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "gi",
  );
  const parts = String(text).split(regex);

  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark
            key={i}
            style={{
              backgroundColor: "#fef3c7",
              color: "#92400e",
              padding: "1px 2px",
              borderRadius: "3px",
              fontWeight: 600,
            }}
          >
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
};

const CountriesListTable = ({
  countries = [],
  onChangeStatus,
  onDelete = () => {},
  onEdit = () => {},
}) => {
  const [search, setSearch] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  const filteredCountries = useMemo(() => {
    if (!search.trim()) return countries;

    const query = search.toLowerCase().trim();
    return countries.filter((item) => {
      const { countryName = "", countryCode = "" } = item ?? {};
      return (
        countryName.toLowerCase().includes(query) ||
        countryCode.toLowerCase().includes(query)
      );
    });
  }, [countries, search]);

  return (
    <div className="mt-5 pt-4">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "16px",
          marginBottom: "20px",
        }}
      >
        <p className="fs-24 fw-400 text-black mb-0" style={{ margin: 0 }}>
          Countries List
        </p>
        <div style={searchContainerStyles}>
          <div style={searchIconStyles}>
            <SearchIcon color={isFocused ? "#6366f1" : "#9ca3af"} />
          </div>
          <input
            id="countries-search-input"
            type="text"
            placeholder="Search by country name or code…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            style={isFocused ? searchInputFocusStyles : searchInputStyles}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              style={clearBtnStyles}
              title="Clear search"
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "#6366f1";
                e.currentTarget.style.backgroundColor = "#f0f0ff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "#9ca3af";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <ClearIcon />
            </button>
          )}
        </div>
      </div>

      {filteredCountries.length ? (
        <table className="table">
          <thead>
            <tr>
              <th className="user-header-item">Country Name</th>
              <th className="user-header-item">Country Code</th>
              <th className="user-header-item header-item-center">createdAt</th>
              <th className="user-header-item header-item-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredCountries.map((item) => {
              const {
                _id: id,
                countryName,
                countryCode,
                createdAt,
              } = item ?? {};
              return (
                <tr className="data-row-verticle-middle" key={id}>
                  <td className="user-data-item data-item-captilize">
                    <HighlightText text={countryName} highlight={search} />
                  </td>
                  <td className="user-data-item text-nowrap">
                    <HighlightText text={countryCode} highlight={search} />
                  </td>
                  <td className="user-data-item data-item-center">
                    {moment(createdAt).format("MMM D, YYYY [at] HH:MM A")}
                  </td>
                  <td className="user-data-item data-item-center ">
                    <button
                      className="btn"
                      onClick={() => {
                        onDelete(id);
                      }}
                    >
                      <AiFillDelete />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div>No matching result found</div>
      )}
    </div>
  );
};

export default CountriesListTable;
